import { server, shared } from '../../config';

export const SHOPIFY_BASE_URL = `https://shopify.com/authentication/${shared.SHOPIFY_SHOP_ID}/`;
export const SHOPIFY_LOGOUT_URL = `${SHOPIFY_BASE_URL}logout`;

// Set only where a hostname cannot be registered with Shopify directly, which
// today means preview environments: the callback allowlist is exact-match with
// no wildcard, and every preview has its own hostname. Those logins go out and
// come back through a single registered URI on this host, which returns the
// customer to the preview that started the login.
export const SHOPIFY_AUTH_PROXY_URL = shared.SHOPIFY_AUTH_PROXY_URL;

// The value Shopify matches against its allowlist. Sent again when the code is
// exchanged, which is why it is one constant rather than two expressions:
// OAuth requires the two to be identical and Shopify rejects the exchange
// outright if they differ.
export const REDIRECT_URI = SHOPIFY_AUTH_PROXY_URL
  ? `${SHOPIFY_AUTH_PROXY_URL}/shopify/callback`
  : `${shared.NEXTAUTH_URL}/api/auth/callback/shopify`;
export const BASIC_HEADERS = {
  'Content-Type': 'application/x-www-form-urlencoded',
  Authorization: `Basic ${btoa(
    `${server.SHOPIFY_CLIENT_ID}:${server.SHOPIFY_CLIENT_SECRET}`,
  )}`,
};

export const obtainToken = async (code, codeVerifier, provider) =>
  await fetch(SHOPIFY_BASE_URL + 'oauth/token', {
    method: 'POST',
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: provider.clientId,
      client_secret: provider.clientSecret,
      redirect_uri: REDIRECT_URI,
      code,
      code_verifier: codeVerifier,
    }),
    headers: BASIC_HEADERS,
  });

// token contains all available tokens
export const refreshShopifyAccessToken = async token => {
  try {
    const response = await fetch(SHOPIFY_BASE_URL + 'oauth/token', {
      method: 'POST',
      body: new URLSearchParams({
        client_id: server.SHOPIFY_CLIENT_ID,
        client_secret: server.SHOPIFY_CLIENT_SECRET,
        grant_type: 'refresh_token',
        refresh_token: token.refresh_token,
      }),
      headers: BASIC_HEADERS,
    });

    const refreshedTokens = await response.json();

    if (!response.ok) {
      throw refreshedTokens;
    }
    return {
      ...token,
      access_token: refreshedTokens.access_token,
      expires_in: refreshedTokens.expires_in,
      refresh_token: refreshedTokens.refresh_token ?? token.refresh_token, // Fall back to old refresh token
    };
  } catch (error) {
    return {
      ...token,
      error: 'RefreshAccessTokenError',
    };
  }
};

// Where a proxy is configured the browser is sent there rather than to Shopify,
// for the same reason REDIRECT_URI is: the logout URI is allowlisted by exact
// string too, so a preview cannot ask to be returned to its own hostname. The
// proxy records which preview this logout belongs to and brings the customer
// back to it.
export const shopifyLogoutRedirect = idToken => {
  // NextAuth has already signed out by the time this is called, so a session
  // that carried no id_token leaves Shopify nothing to end. Sending it one it
  // cannot read — `undefined`, which is what URLSearchParams serialises — is
  // answered with an error page rather than a redirect, and the customer never
  // arrives back.
  if (!idToken) {
    return SHOPIFY_AUTH_PROXY_URL
      ? shared.NEXTAUTH_URL
      : `https://${shared.DOMAIN_PUBLIC_NAME}/`;
  }

  if (SHOPIFY_AUTH_PROXY_URL) {
    const proxyParams = new URLSearchParams({
      id_token_hint: idToken,
      ollie_rd: shared.NEXTAUTH_URL,
      ollie_shop_id: shared.SHOPIFY_SHOP_ID,
    });
    return `${SHOPIFY_AUTH_PROXY_URL}/shopify/logout?${proxyParams}`;
  }

  const logoutRedirectUrl =
    `${SHOPIFY_LOGOUT_URL}?` +
    new URLSearchParams({
      id_token_hint: idToken,
      post_logout_redirect_uri: `https://${shared.DOMAIN_PUBLIC_NAME}/`,
    });
  return logoutRedirectUrl;
};

// this API call is used to fetch the customer UUID
export const getCustomerUUID = async (id, accessToken) => {
  const response = await fetch(
    `${shared.API_HTTP_SCHEME}://${shared.API_BASE_URL}/api/v3/customers/${id}/`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'x-ollie-user-id': `${id}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`getCustomerUUID failed: ${response.status}`);
  }

  const data = await response.json();

  if (!data.id) {
    throw new Error('getCustomerUUID: response missing id');
  }

  return {
    id: data.id,
    is_active: data.is_active,
  };
};
